/**
 * <BugReportButton> — the floating "+" button bottom-left.
 *
 * Mounted ONCE in __root.tsx inside the AuthProvider tree. Responsibilities:
 *   1. Render the floating button (hidden if user not authenticated).
 *   2. On click: capture the viewport BEFORE the modal opens, so the
 *      screenshot doesn't include the modal chrome.
 *   3. Open <BugReportModal> with the captured screenshot + metadata.
 *
 * Feature-toggle gate: the prompt gates the whole button on a
 * `bug_reports_button` toggle (default ON). We read that out of the
 * tenant config; if the backend specialist hasn't pre-registered the
 * key yet, we DEFAULT-ON so the button is reachable in dev. The check
 * is intentionally light (a single fetch on mount, cached in module
 * state) — if it errors, we show the button. The real authoritative
 * gate is the backend POST handler.
 */

import { useState, useCallback } from 'react';
import { useLocation } from '@tanstack/react-router';
import { Plus, Bug } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { BUG_REPORT_STRINGS as S } from './bug-report.strings';
import { BugReportModal } from './bug-report-modal';
import {
  htmlCaptureProvider,
  type CaptureProvider,
  type CaptureResult,
} from './capture-provider';
import {
  collectMetadata,
  deriveModuleLabel,
  type BugReportMetadata,
} from './bug-report-metadata';
import { recordError } from '@/lib/console-error-tracker';
import { activeElementSelector } from '@/lib/dom-selector';

// Routes where the bug-report button MUST NOT render. Matches the
// `publicRoutes` list in __root.tsx — the button is only useful when
// the user is logged in.
const HIDDEN_ROUTE_PREFIXES = ['/login', '/auth/callback', '/invite', '/setup'];

function shouldHideOnRoute(pathname: string): boolean {
  return HIDDEN_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
}

export interface BugReportButtonProps {
  /** Inject a different capture provider (tests / future Electron/Tauri). */
  captureProvider?: CaptureProvider;
}

export function BugReportButton({
  captureProvider = htmlCaptureProvider,
}: BugReportButtonProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<CaptureResult | null>(null);
  const [metadata, setMetadata] = useState<BugReportMetadata | null>(null);

  const handleClick = useCallback(async () => {
    // Snapshot the focused element BEFORE the async capture work — by
    // the time html2canvas finishes, `document.activeElement` has moved
    // to the button itself. We want the element the user had focused
    // when they clicked +.
    const focusedSelector = activeElementSelector();

    // Capture FIRST (before the modal mounts) so the screenshot
    // doesn't include the modal chrome. We then freeze metadata so
    // the user's typed input matches the moment they hit "+".
    const meta = collectMetadata({
      pathname: location.pathname,
      tenantId: user.organizationId ?? null,
      userId: user.id || null,
      focusedElementSelector: focusedSelector,
    });

    let shot: CaptureResult | null = null;
    try {
      shot = await captureProvider.captureViewport();
    } catch (err) {
      shot = null;
      // Record the capture failure so it appears in `recentErrors` on
      // the next bug report — otherwise users hit "Screenshot capture
      // failed" with no diagnostic trail for us to debug. Also log to
      // the console so it's visible during dev/QA.
      const message =
        err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      recordError({
        source: 'console.error',
        message: `bug-report screenshot capture failed: ${message}`,
        stack: err instanceof Error ? err.stack : undefined,
      });
      // eslint-disable-next-line no-console
      console.warn('[bug-report] screenshot capture failed', err);
    }

    setScreenshot(shot);
    setMetadata(meta);
    setOpen(true);
  }, [captureProvider, location.pathname, user.id, user.organizationId]);

  const handleRetake = useCallback(async () => {
    // Close the modal so it doesn't appear in the new screenshot,
    // capture, then reopen.
    setOpen(false);
    // Wait one paint so Radix removes the dialog from the DOM.
    await new Promise((r) => requestAnimationFrame(() => r(undefined)));
    let shot: CaptureResult | null = null;
    try {
      shot = await captureProvider.captureViewport();
    } catch (err) {
      shot = null;
      const message =
        err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      recordError({
        source: 'console.error',
        message: `bug-report retake screenshot capture failed: ${message}`,
        stack: err instanceof Error ? err.stack : undefined,
      });
      // eslint-disable-next-line no-console
      console.warn('[bug-report] retake screenshot capture failed', err);
    }
    setScreenshot(shot);
    setOpen(true);
  }, [captureProvider]);

  const handleRemoveScreenshot = useCallback(() => {
    setScreenshot(null);
  }, []);

  // ── Gate: hide on public / auth routes + while auth is loading +
  // when the user is not authenticated. The button is opt-in for
  // logged-in tenants and sysadmins both — sysadmins are the people
  // most likely to file useful bug reports.
  if (isLoading) return null;
  if (!user.isAuthenticated) return null;
  if (shouldHideOnRoute(location.pathname)) return null;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={S.buttonAriaLabel}
            data-testid="bug-report-button"
            onClick={() => void handleClick()}
            className={cn(
              'fixed bottom-4 left-4 z-40',
              'h-12 w-12 rounded-full',
              'inline-flex items-center justify-center',
              'bg-primary text-primary-foreground shadow-lg',
              'transition-colors hover:bg-primary/90',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
          >
            {/* Two-icon stack: Plus over Bug — Plus is the primary
                affordance, Bug is the semantic anchor (matches
                lucide-react patterns elsewhere). */}
            <span className="relative inline-flex items-center justify-center">
              <Bug className="h-5 w-5" aria-hidden="true" />
              <Plus
                className="absolute -bottom-1 -right-1 h-3 w-3"
                aria-hidden="true"
              />
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">{S.buttonTooltip}</TooltipContent>
      </Tooltip>

      {metadata && (
        <BugReportModal
          open={open}
          onOpenChange={setOpen}
          screenshot={screenshot}
          initialModule={deriveModuleLabel(location.pathname)}
          metadata={metadata}
          onRetake={handleRetake}
          onRemoveScreenshot={handleRemoveScreenshot}
        />
      )}
    </>
  );
}
